import React, { useState, useEffect } from 'react';
import { CouponManagerProps, Coupon, CouponCreateData, CouponUpdateData, CouponSettings } from '../../types';
import Button from '../atoms/Button';
import CouponToggle from '../atoms/CouponToggle';
import CouponForm from '../molecules/CouponForm';
import CouponList from '../molecules/CouponList';
import { couponService } from '../../services/couponService';
import './CouponManager.css';

const CouponManager: React.FC<CouponManagerProps> = ({
  coupons,
  settings,
  loading = false,
  onCreateCoupon,
  onUpdateCoupon,
  onDeleteCoupon,
  onToggleCouponStatus,
  onUpdateSettings
}) => {
  const [activeView, setActiveView] = useState<'list' | 'create' | 'edit'>('list');
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreateCoupon = async (couponData: CouponCreateData | CouponUpdateData) => {
    try {
      setIsLoading(true);
      setError(null);
      await onCreateCoupon(couponData as CouponCreateData);
      setActiveView('list');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create coupon');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateCoupon = async (couponId: string, updates: CouponUpdateData) => {
    try {
      setIsLoading(true);
      setError(null);
      await onUpdateCoupon(couponId, updates);
      setActiveView('list');
      setSelectedCoupon(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update coupon');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCoupon = async (coupon: Coupon) => {
    if (window.confirm(`Are you sure you want to delete the coupon "${coupon.name}"? This action cannot be undone.`)) {
      try {
        setIsLoading(true);
        setError(null);
        await onDeleteCoupon(coupon.id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete coupon');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleToggleCouponStatus = async (coupon: Coupon) => {
    try {
      setIsLoading(true);
      setError(null);
      await onToggleCouponStatus(coupon.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle coupon status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCoupon = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setActiveView('edit');
  };

  const handleCancel = () => {
    setActiveView('list');
    setSelectedCoupon(null);
    setError(null);
  };

  const handleSettingsToggle = async (enabled: boolean) => {
    try {
      setIsLoading(true);
      setError(null);
      await onUpdateSettings({ isEnabled: enabled });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update settings');
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    if (activeView === 'create') {
      return (
        <CouponForm
          onSubmit={handleCreateCoupon}
          onCancel={handleCancel}
          loading={isLoading}
        />
      );
    }

    if (activeView === 'edit' && selectedCoupon) {
      return (
        <CouponForm
          coupon={selectedCoupon}
          onSubmit={(updates) => handleUpdateCoupon(selectedCoupon.id, updates)}
          onCancel={handleCancel}
          loading={isLoading}
        />
      );
    }

    return (
      <div className="coupon-manager__list-view">
        <div className="coupon-manager__list-header">
          <div className="coupon-manager__list-title">
            <h2>Coupon Management</h2>
            <p>Create and manage discount coupons for your customers.</p>
          </div>
          <Button
            variant="primary"
            onClick={() => setActiveView('create')}
            disabled={loading || isLoading}
          >
            Create New Coupon
          </Button>
        </div>

        <CouponList
          coupons={coupons}
          onEdit={handleEditCoupon}
          onDelete={handleDeleteCoupon}
          onToggleStatus={handleToggleCouponStatus}
          loading={loading || isLoading}
        />
      </div>
    );
  };

  return (
    <div className="coupon-manager">
      {error && (
        <div className="coupon-manager__error">
          <div className="coupon-manager__error-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="coupon-manager__error-content">
            <h4>Error</h4>
            <p>{error}</p>
          </div>
          <button
            className="coupon-manager__error-dismiss"
            onClick={() => setError(null)}
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}

      <div className="coupon-manager__settings">
        <CouponToggle
          isEnabled={settings?.isEnabled || false}
          onToggle={handleSettingsToggle}
          loading={isLoading}
        />
      </div>

      <div className="coupon-manager__content">
        {renderContent()}
      </div>
    </div>
  );
};

export default CouponManager;
